import { AnimatePresence, motion, MotionConfig, useMotionValue } from 'framer-motion';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Users from 'lucide-react/dist/esm/icons/users';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';
import {
	getEventCardDateLabel,
	getEventCardGuestLabel,
	isEventVisibleForCard,
	resolvedEventBookingType,
	TDR
} from 'tdr-common';
import { EventBookNowButton } from '../events/EventBookNowButton';
import { EventBookNowOnResultsButton } from '../events/EventBookNowOnResultsButton';
import { getEventCdnImageUri } from '../../helpers/getCDNURI';
import { AvailabilityGuardContext, SearchFilters } from '../../routes/AvailabilityGuard';
import { formatBetweenTimes, getNonRecurringDatesDetailLines, getEventImagePath } from '../events/EventCard';
import styles from './EventCarousel.module.scss';

export { isEventVisibleForCard };

const DRAG_BUFFER = 10;

type EventCarouselProps = {
	items: TDR.Event[];
};

export const EventCarousel = ({ items }: EventCarouselProps) => {
	const ctx = useOutletContext<AvailabilityGuardContext>();
	const { restaurantSlug: slugFromRoute } = useParams<{ restaurantSlug: string }>();
	const restaurant = ctx?.restaurant;
	const tables = ctx?.tables ?? [];
	const searchFilters = ctx?.searchFilters;
	const updateSearchParams = ctx?.updateSearchParams;
	const restaurantSlug = restaurant?.slug ?? slugFromRoute ?? '';
	const restaurantId = restaurant?.id ?? '';
	const timezone = restaurant?.timezone;

	const visibleItems = items.filter(isEventVisibleForCard);
	const [index, setIndex] = useState(0);

	useEffect(() => {
		setIndex((prev) => Math.min(prev, Math.max(0, visibleItems.length - 1)));
	}, [visibleItems.length]);

	const dragX = useMotionValue(0);

	const safeIndex = visibleItems.length === 0 ? 0 : Math.min(index, visibleItems.length - 1);

	const onDragEnd = () => {
		const x = dragX.get();

		if (x <= -DRAG_BUFFER && safeIndex < visibleItems.length - 1) {
			setIndex((prev) => prev + 1);
		}
		else if (x >= DRAG_BUFFER && safeIndex > 0) {
			setIndex((prev) => prev - 1);
		}
	};

	const handlePrev = () => setIndex((prev) => (prev === 0 ? Math.max(0, visibleItems.length - 1) : prev - 1));

	const handleNext = () =>
		setIndex((prev) => (prev === visibleItems.length - 1 ? 0 : prev + 1));

	if (visibleItems.length === 0) {
		return null;
	}

	return (
		<MotionConfig transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}>
			<div className={styles.Container}>
				<motion.div
					drag='x'
					dragConstraints={{
						left: 0,
						right: 0
					}}
					style={{
						x: dragX
					}}
					animate={{ translateX: `-${safeIndex * 100}%` }}
					onDragEnd={onDragEnd}
					className={styles.Items}
				>
					{visibleItems.map((event, i) => (
						<EventCard
							event={event}
							isActive={safeIndex === i}
							key={event.id}
							restaurantSlug={restaurantSlug}
							tables={tables}
							restaurantId={restaurantId}
							timezone={timezone}
							searchFilters={searchFilters}
							updateSearchParams={updateSearchParams}
						/>
					))}
				</motion.div>

				{safeIndex > 0 && (
					<AnimatePresence initial={false}>
						<motion.button
							initial={{ opacity: 0 }}
							animate={{ opacity: 0.9 }}
							exit={{ opacity: 0, pointerEvents: 'none' }}
							className={`${styles.Nav} ${styles['Nav--prev']}`}
							whileHover={{ opacity: 1 }}
							onClick={handlePrev}
							tabIndex={0}
						>
							<ChevronLeft />
						</motion.button>
					</AnimatePresence>
				)}

				{safeIndex + 1 < visibleItems.length && (
					<AnimatePresence initial={false}>
						<motion.button
							initial={{ opacity: 0 }}
							animate={{ opacity: 0.9 }}
							exit={{ opacity: 0, pointerEvents: 'none' }}
							className={`${styles.Nav} ${styles['Nav--next']}`}
							whileHover={{ opacity: 1 }}
							onClick={handleNext}
							tabIndex={0}
						>
							<ChevronRight />
						</motion.button>
					</AnimatePresence>
				)}
			</div>
		</MotionConfig>
	);
};

const EventCard = ({
	event,
	isActive,
	restaurantSlug,
	tables,
	restaurantId,
	timezone,
	searchFilters,
	updateSearchParams
}: {
	event: TDR.Event;
	isActive: boolean;
	restaurantSlug: string;
	tables: TDR.Table[];
	restaurantId: string;
	timezone?: string;
	searchFilters?: SearchFilters;
	updateSearchParams?: AvailabilityGuardContext['updateSearchParams'];
}) => {
	const { t } = useTranslation();
	const [showDetails, setShowDetails] = useState(false);

	const filePath = getEventImagePath(event.id);

	const eventInfo = event.displays[0];
	const eventCondition = event.condition?.value;

	const timeInfo = eventCondition?.betweenTimes ? formatBetweenTimes(eventCondition.betweenTimes) : null;
	const nonRecurringDateLines = getNonRecurringDatesDetailLines(event);

	const dateLabel = getEventCardDateLabel(event);
	const guestLabel = getEventCardGuestLabel(event);

	const hasExpandableDetails =
		(nonRecurringDateLines?.length ?? 0) > 0 || eventInfo.description.length > 0;

	const toggleShowDetails = () => setShowDetails((prev) => !prev);

	const imageVariants = {
		collapsed: { height: 0, opacity: 0 },
		expanded: { height: 240, opacity: 1 }
	};

	const infoVariants = {
		collapsed: { paddingTop: '8px' },
		expanded: { paddingTop: '52px' }
	};

	const descriptionVariants = {
		collapsed: { height: 0, opacity: 0 },
		expanded: { height: 'auto', opacity: 1 }
	};

	const isTableEvent = resolvedEventBookingType(event) === 'table';
	const canSelectParamEvent = !isTableEvent && !!searchFilters && !!updateSearchParams;

	const bookControl = isTableEvent ? (
		<EventBookNowButton
			event={event}
			restaurantSlug={restaurantSlug}
			tables={tables}
			restaurantId={restaurantId}
			timezone={timezone ?? null}
			className={styles.Link}
			disabledClassName={styles.LinkDisabled}
			tabIndex={isActive ? 0 : -1}
		/>
	) : canSelectParamEvent ? (
		<EventBookNowOnResultsButton
			event={event}
			tables={tables}
			restaurantId={restaurantId}
			timezone={timezone ?? null}
			currentFilters={searchFilters}
			updateSearchParams={updateSearchParams}
			className={styles.Link}
			selectedClassName={styles.LinkSelected}
			disabledClassName={styles.LinkDisabled}
			tabIndex={isActive ? 0 : -1}
		/>
	) : (
		// Defensive fallback for the (currently unreachable) case where this card renders
		// outside the AvailabilityGuard Outlet context, so searchFilters/updateSearchParams
		// aren't available for the select-event flow.
		<EventBookNowButton
			event={event}
			restaurantSlug={restaurantSlug}
			tables={tables}
			restaurantId={restaurantId}
			timezone={timezone ?? null}
			className={styles.Link}
			disabledClassName={styles.LinkDisabled}
			tabIndex={isActive ? 0 : -1}
			label={t('actions.select_event', { defaultValue: 'Select Event' })}
		/>
	);

	return (
		<div className={styles.Card} tabIndex={-1}>
			<motion.img
				variants={imageVariants}
				animate={showDetails ? 'collapsed' : 'expanded'}
				src={getEventCdnImageUri(filePath)}
				className={styles.Image}
			/>
			<motion.div variants={infoVariants} animate={showDetails ? 'expanded' : 'collapsed'} className={styles.Info}>
				<p className={styles.Name}>{eventInfo.title}</p>

				<p className={styles.Details}>
					{dateLabel ? (
						<span>
							<CalendarDays size={20} /> {dateLabel}
						</span>
					) : null}
					{timeInfo ? (
						<>
							{dateLabel ? ' • ' : null}
							<span>
								<Clock size={20} /> {timeInfo}
							</span>
						</>
					) : null}
					{guestLabel ? (
						<>
							{dateLabel || timeInfo ? ' • ' : null}
							<span>
								<Users size={20} /> {guestLabel}
							</span>
						</>
					) : null}
				</p>

				<motion.div
					variants={descriptionVariants}
					animate={showDetails ? 'expanded' : 'collapsed'}
					className={styles.Description}
				>
					{nonRecurringDateLines?.length ? (
						<div className={styles.DescriptionDatesBlock}>
							<p className={styles.DescriptionDatesLabel}>Dates</p>
							<p className={styles.DescriptionDatesLine}>
								{nonRecurringDateLines.map((line, i) => (
									<React.Fragment key={`${i}-${line}`}>
										{i > 0 ? (
											<span className={styles.DescriptionDatesSep} aria-hidden>
												{' | '}
											</span>
										) : null}
										<span className={styles.DescriptionDatesToken}>{line}</span>
									</React.Fragment>
								))}
							</p>
						</div>
					) : null}
					{eventInfo.description.length > 0 ? (
						<p className={styles.DescriptionBody}>{eventInfo.description}</p>
					) : null}
				</motion.div>
			</motion.div>

			{hasExpandableDetails && (
				<button className={styles.Toggle} onClick={toggleShowDetails} tabIndex={isActive ? 0 : -1}>
					{showDetails ? 'Hide' : 'Show'} Details
				</button>
			)}
			{bookControl}
		</div>
	);
};
